import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  FormControlLabel,
  Divider,
  Alert,
  Modal,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@esgian/esgianui';
import { useSegment } from '@hooks';
import PropTypes from 'prop-types';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { useSelector } from 'react-redux';
import { GEOGRAPHICAL_TYPES } from '@constants';
import moment from 'moment';
import { DateRangePicker } from '@components/Inputs';
import { exportCSVFile, exportExcelFile } from '@helpers';
import { convertCanalDeparturesData, getCanalDepartures, getLatestPortCalls } from '@api';
import { toast } from 'react-toastify';

function DownloadDateRangeModal({
  showModal = false,
  setShowModal = null,
  vesselImo = null,
  geoId = null,
  name = '',
  ownerId = null,
  oprId = null,
  columns = [],
  geographicalType = GEOGRAPHICAL_TYPES.ALL
}) {
  const [selectedType, setSelectedType] = useState(1);
  const [fileFormat, setFileFormat] = useState(1);
  const [period, setPeriod] = useState({
    endDate: moment.utc(),
    startDate: moment.utc().subtract(1, 'month').startOf('month')
  });
  const { segmentMinDate, segment } = useSegment();
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const [errorMessages, setErrorMessages] = useState('');

  const getPortCallCustomRangeData = async (isDownloadAll) => {
    const controller = new AbortController();
    const { signal } = controller;
    setErrorMessages('');
    let apiCall;
    const body = {
      operatorTypeId: operatorType,
      segmentTypeId: segment.id,
      commercialCategoryId: commercialCategoryType
    };
    let start = isDownloadAll
      ? moment.utc(segmentMinDate).toJSON()
      : moment.utc(period.startDate).startOf('day').toJSON();
    const end = isDownloadAll
      ? moment.utc().toJSON()
      : moment.utc(period.endDate).endOf('day').toJSON();

    if (geographicalType === GEOGRAPHICAL_TYPES.CANAL) {
      apiCall = getCanalDepartures(
        {
          ...body,
          start: start,
          end: end,
          canalIds: [geoId]
        },
        signal
      );
    } else {
      apiCall = getLatestPortCalls(
        {
          ...body,
          geographicalType: geographicalType, // This should maybe be a prop based on where it is used
          startDate: start,
          endDate: end,
          imos: vesselImo ? [vesselImo] : [],
          operators: oprId ? [oprId] : [],
          owners: ownerId ? [ownerId] : [],
          geoIds: geoId ? [geoId] : []
        },
        signal
      );
    }
    apiCall
      .then((result) => {
        if (!result || result.length === 0) {
          setErrorMessages('No data available for the selected date range.');
          return;
        }
        let res = result;
        if (geographicalType === GEOGRAPHICAL_TYPES.CANAL) {
          res = convertCanalDeparturesData(result);
        }
        if (fileFormat === 1) {
          exportCSVFile(columns, res, name);
        } else {
          exportExcelFile(columns, res, name);
        }
        toast.success('Download success', {
          position: 'top-right'
        });
        setShowModal(false);
      })
      .catch((e) => {
        console.log(e);
        setErrorMessages('No data available for the selected date range. Please try again.');
      });
  };

  return (
    <Modal
      sx={{ p: 4 }}
      handleClose={() => setShowModal(false)}
      title={'Export'}
      open={showModal}
      size={'sm'}>
      <Stack spacing={2}>
        <Stack>
          <Tabs
            value={selectedType}
            onChange={(_, val) => setSelectedType(val)}
            variant={'fullWidth'}>
            <Tab value={1} label={<Typography variant={'body2'}>Custom period</Typography>} />
            <Tab value={2} label={<Typography variant={'body2'}>Download all</Typography>} />
          </Tabs>
          <Divider />
        </Stack>
        {errorMessages && <Alert severity={'info'}>{errorMessages}</Alert>}
        <FormControl>
          <FormLabel>
            <Typography color={'text.primary'} variant={'body2'}>
              File format
            </Typography>
          </FormLabel>
          <RadioGroup
            row
            value={fileFormat}
            onChange={({ target }) => {
              setFileFormat(Number(target.value));
            }}>
            <FormControlLabel
              value={1}
              control={<Radio size={'small'} />}
              label={<Typography variant={'body2'}>CSV</Typography>}
            />
            <FormControlLabel
              value={2}
              control={<Radio size={'small'} />}
              label={<Typography variant={'body2'}>Excel</Typography>}
            />
          </RadioGroup>
        </FormControl>
        {selectedType === 1 && (
          <Stack>
            <Typography variant={'body2'}>Period</Typography>
            <DateRangePicker
              InputPropsSx={{ maxWidth: '250px' }}
              inputSize={'small'}
              startDate={period.startDate}
              endDate={period.endDate}
              onClose={(start, end) => {
                // Using utc(true) to correctly convert the local time from the DatePicker into UTC.
                // This ensures that a date like '2025-03-01T00:00:00' (local time) becomes
                // '2025-03-01T00:00:00Z' in UTC instead of shifting to the previous day like a time-traveling gremlin.

                setPeriod({
                  startDate: moment(start).startOf('day').utc(true),
                  endDate: moment(end).endOf('day').utc(true)
                });
              }}
            />
          </Stack>
        )}
        <Divider />
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={2}>
          <Button variant={'text'} onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!geoId && !oprId && !ownerId && !vesselImo) return;
              getPortCallCustomRangeData(selectedType === 2);
            }}
            variant="contained">
            Download
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

DownloadDateRangeModal.propTypes = {
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func.isRequired,
  vesselImo: PropTypes.number,
  columns: PropTypes.arrayOf(PropTypes.object),
  geoId: PropTypes.number,
  name: PropTypes.string,
  ownerId: PropTypes.number,
  oprId: PropTypes.number,
  geographicalType: PropTypes.oneOf(Object.values(GEOGRAPHICAL_TYPES))
};

export default DownloadDateRangeModal;
